import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBetSlip } from '../../components/feature/BetSlipContext';
import PageHero from '../../components/feature/PageHero';

interface BetOption {
  name: string;
  prob: string;
  price: string;
  payout: string;
  profit: string;
}

interface SuperBowlBetCardProps {
  badge: string;
  badgeColor: string;
  category: string;
  title: string;
  description: string;
  volume24h: string;
  totalVolume: string;
  options: BetOption[];
}

function SuperBowlBetCard({ badge, badgeColor, category, title, description, volume24h, totalVolume, options }: SuperBowlBetCardProps) {
  const [selectedOption, setSelectedOption] = useState(options[0].name);
  const [added, setAdded] = useState(false);
  const { addBet } = useBetSlip();

  const selected = options.find(o => o.name === selectedOption) || options[0];

  // Convert probability string (e.g. "53%") to "$1 to win $X.XX" format
  const formatOdds = (prob: string) => {
    const p = parseFloat(prob) / 100;
    if (!p || p <= 0) return { win: '$1.00', profit: '+$0.00' };
    const payout = (1 / p);
    const profit = payout - 1;
    return {
      win: `$${payout.toFixed(2)}`,
      profit: `+$${profit.toFixed(2)}`,
    };
  };

  const handleAdd = () => {
    const odds = formatOdds(selected.prob);
    addBet({
      id: `sb-${title}-${selected.name}`.replace(/\s+/g, '-').toLowerCase(),
      market: `Super Bowl LIX — ${category}`,
      matchup: title,
      pick: selected.name,
      price: selected.price,
      payout: odds.win,
      profit: odds.profit,
      badge: 'SB LIX',
      badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl border border-slate-700/80 shadow-xl overflow-hidden hover:border-slate-500/80 hover:shadow-2xl hover:shadow-teal-500/5 transition-all flex flex-col h-full">
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-3">
          <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold tracking-wide border ${badgeColor}`}>
            <i className="ri-football-line"></i>
            {badge}
          </span>
          <span className="text-xs text-slate-500 bg-slate-800/60 px-2 py-1 rounded-full border border-slate-700/50">{category}</span>
        </div>
        <div className="mb-4">
          <h3 className="text-lg font-bold text-white mb-1 leading-tight">{title}</h3>
          <p className="text-sm text-slate-400 line-clamp-2">{description}</p>
        </div>
        <div className="flex items-center gap-3 mb-4 p-2.5 bg-white/5 rounded-lg border border-slate-700/50">
          <div className="flex items-center gap-1.5">
            <i className="ri-line-chart-line text-teal-500 text-sm"></i>
            <span className="text-xs text-slate-400">24h Vol:</span>
            <span className="text-xs font-bold text-teal-500">{volume24h}</span>
          </div>
          <div className="w-px h-4 bg-slate-700"></div>
          <div className="flex items-center gap-1.5">
            <i className="ri-water-flash-line text-emerald-400 text-sm"></i>
            <span className="text-xs text-slate-400">Total:</span>
            <span className="text-xs font-bold text-emerald-400">{totalVolume}</span>
          </div>
        </div>
        <div className="flex-1 bg-white/5 rounded-lg p-3 border border-slate-700/50 mb-4 overflow-y-auto max-h-[200px]">
          <div className="space-y-2">
            {options.map((option, idx) => {
              const odds = formatOdds(option.prob);
              return (
                <div
                  key={idx}
                  onClick={() => setSelectedOption(option.name)}
                  className={`cursor-pointer p-2 rounded border transition-colors flex justify-between items-center ${
                    option.name === selectedOption
                      ? 'bg-teal-500/10 border-teal-500/50'
                      : 'bg-white/5 border-slate-700/80 hover:border-slate-500'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${option.name === selectedOption ? 'text-teal-400' : 'text-slate-300'}`}>
                      {option.name}
                    </div>
                    <div className="text-xs text-slate-500">Prob: {option.prob}</div>
                  </div>
                  <div className="text-right pl-2 flex flex-col items-end">
                    <div className="text-xs text-slate-400">$1 to win</div>
                    <div className="text-xs text-white font-bold">{odds.win}</div>
                    <div className="text-xs text-teal-500 font-medium">{odds.profit}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-auto pt-3 border-t border-slate-700/50">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-400">Selected</div>
              <div className="text-sm font-bold text-white truncate">{selected.name}</div>
              <div className="text-xs text-slate-400">
                <span>$1 to win </span>
                <span className="text-white font-semibold">{formatOdds(selected.prob).win}</span>
                <span className="mx-1">•</span>
                <span className="text-teal-500 font-semibold">{formatOdds(selected.prob).profit}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                className={`inline-flex items-center justify-center text-sm h-9 rounded-md px-4 font-bold shadow-lg cursor-pointer whitespace-nowrap transition-all ${
                  added
                    ? 'bg-emerald-500 text-white shadow-emerald-500/25'
                    : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white shadow-teal-500/25'
                }`}
              >
                {added ? <><i className="ri-check-line mr-1"></i><span>Added</span></> : <><span className="hidden sm:inline">Add to Slip</span><span className="sm:hidden">Add</span></>}
              </button>
              <button className="inline-flex items-center justify-center text-sm font-medium border h-9 rounded-md border-slate-600 hover:bg-slate-800 text-slate-300 px-2 cursor-pointer transition-colors" title="View Details">
                <i className="ri-scales-3-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const BADGE: Record<string, string> = {
  gold:   'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  teal:   'bg-teal-500/20 text-teal-400 border-teal-500/30',
  cyan:   'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  rose:   'bg-rose-500/20 text-rose-400 border-rose-500/30',
  amber:  'bg-amber-500/20 text-amber-400 border-amber-500/30',
  violet: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  lime:   'bg-lime-500/20 text-lime-400 border-lime-500/30',
  sky:    'bg-sky-500/20 text-sky-400 border-sky-500/30',
  green:  'bg-green-500/20 text-green-400 border-green-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const allMarkets: Record<string, SuperBowlBetCardProps[]> = {
  'Game Props': [
    {
      badge: 'Game Spread', badgeColor: BADGE.teal, category: 'Game Props',
      title: 'Game Spread',
      description: 'Will the Patriots or Seahawks cover the spread in Super Bowl LIX?',
      volume24h: '$312.4K', totalVolume: '$2.1M',
      options: [
        { name: 'Patriots +4.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Seahawks -4.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
    {
      badge: 'Game Total Points', badgeColor: BADGE.amber, category: 'Game Props',
      title: 'Game Total Points',
      description: 'Will the combined score go over or under the total in Super Bowl LIX?',
      volume24h: '$287.6K', totalVolume: '$1.8M',
      options: [
        { name: 'Over 45.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 45.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Seahawks Team Total', badgeColor: BADGE.cyan, category: 'Game Props',
      title: 'Seahawks Team Total',
      description: 'Total points scored by Seattle in Super Bowl LIX.',
      volume24h: '$198.3K', totalVolume: '$1.2M',
      options: [
        { name: 'Over 25.0', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
        { name: 'Under 25.0', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
      ],
    },
    {
      badge: 'Patriots Team Total', badgeColor: BADGE.rose, category: 'Game Props',
      title: 'Patriots Team Total',
      description: 'Total points scored by New England in Super Bowl LIX.',
      volume24h: '$142.1K', totalVolume: '$688K',
      options: [
        { name: 'Over 20.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
        { name: 'Under 20.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      ],
    },
    {
      badge: 'First Half Total', badgeColor: BADGE.sky, category: 'Game Props',
      title: 'First Half Total Points',
      description: 'Combined points in first half of Super Bowl LIX.',
      volume24h: '$118.4K', totalVolume: '$541K',
      options: [
        { name: 'Over 22.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 22.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Second Half Total', badgeColor: BADGE.violet, category: 'Game Props',
      title: 'Second Half Total Points',
      description: 'Combined points in second half of Super Bowl LIX.',
      volume24h: '$97.2K', totalVolume: '$412K',
      options: [
        { name: 'Over 22.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 22.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Highest Scoring Quarter', badgeColor: BADGE.gold, category: 'Game Props',
      title: 'Highest Scoring Quarter',
      description: 'Which quarter will have the most combined points in Super Bowl LIX?',
      volume24h: '$88.1K', totalVolume: '$374K',
      options: [
        { name: '1st Quarter', prob: '22%', price: '$0.22', payout: '$1.00', profit: '+$0.78' },
        { name: '2nd Quarter', prob: '31%', price: '$0.31', payout: '$1.00', profit: '+$0.69' },
        { name: '3rd Quarter', prob: '19%', price: '$0.19', payout: '$1.00', profit: '+$0.81' },
        { name: '4th Quarter', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      ],
    },
    {
      badge: 'Will Game Go To OT?', badgeColor: BADGE.orange, category: 'Game Props',
      title: 'Will Game Go To Overtime?',
      description: 'Will Super Bowl LIX require overtime to determine a winner?',
      volume24h: '$78.1K', totalVolume: '$312K',
      options: [
        { name: 'No', prob: '82%', price: '$0.82', payout: '$1.00', profit: '+$0.18' },
        { name: 'Yes', prob: '18%', price: '$0.18', payout: '$1.00', profit: '+$0.82' },
      ],
    },
    {
      badge: 'Will There Be a Safety?', badgeColor: BADGE.rose, category: 'Game Props',
      title: 'Will There Be a Safety?',
      description: 'Will either team score a safety at any point in Super Bowl LIX?',
      volume24h: '$42.6K', totalVolume: '$188K',
      options: [
        { name: 'Yes', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
        { name: 'No', prob: '86%', price: '$0.86', payout: '$1.00', profit: '+$0.14' },
      ],
    },
  ],
  'Player Props': [
    {
      badge: 'Sam Darnold Passing Yards', badgeColor: BADGE.cyan, category: 'Player Props',
      title: 'Sam Darnold Passing Yards',
      description: 'Total passing yards by Sam Darnold in Super Bowl LIX.',
      volume24h: '$154.2K', totalVolume: '$720K',
      options: [
        { name: 'Over 230.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 230.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Sam Darnold Passing TDs', badgeColor: BADGE.cyan, category: 'Player Props',
      title: 'Sam Darnold Passing TDs',
      description: 'Total touchdown passes by Sam Darnold in Super Bowl LIX.',
      volume24h: '$128.6K', totalVolume: '$591K',
      options: [
        { name: 'Over 1.5', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
        { name: 'Under 1.5', prob: '42%', price: '$0.42', payout: '$1.00', profit: '+$0.58' },
      ],
    },
    {
      badge: 'Sam Darnold Interceptions', badgeColor: BADGE.rose, category: 'Player Props',
      title: 'Sam Darnold Interceptions',
      description: 'Total interceptions thrown by Sam Darnold in Super Bowl LIX.',
      volume24h: '$94.1K', totalVolume: '$428K',
      options: [
        { name: 'Over 0.5', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
        { name: 'Under 0.5', prob: '56%', price: '$0.56', payout: '$1.00', profit: '+$0.44' },
      ],
    },
    {
      badge: 'Sam Darnold Pass Attempts', badgeColor: BADGE.sky, category: 'Player Props',
      title: 'Sam Darnold Pass Attempts',
      description: 'Total pass attempts by Sam Darnold in Super Bowl LIX.',
      volume24h: '$88.3K', totalVolume: '$374K',
      options: [
        { name: 'Over 30.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Under 30.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
    {
      badge: 'Sam Darnold Completions', badgeColor: BADGE.teal, category: 'Player Props',
      title: 'Sam Darnold Completions',
      description: 'Total completions by Sam Darnold in Super Bowl LIX.',
      volume24h: '$76.4K', totalVolume: '$341K',
      options: [
        { name: 'Over 20.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 20.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Drake Maye Passing Yards', badgeColor: BADGE.amber, category: 'Player Props',
      title: 'Drake Maye Passing Yards',
      description: 'Total passing yards by Drake Maye in Super Bowl LIX.',
      volume24h: '$132.8K', totalVolume: '$598K',
      options: [
        { name: 'Over 220.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 220.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Drake Maye Passing TDs', badgeColor: BADGE.amber, category: 'Player Props',
      title: 'Drake Maye Passing TDs',
      description: 'Total touchdown passes by Drake Maye in Super Bowl LIX.',
      volume24h: '$101.3K', totalVolume: '$462K',
      options: [
        { name: 'Over 1.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
        { name: 'Under 1.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
      ],
    },
    {
      badge: 'Drake Maye Interceptions', badgeColor: BADGE.rose, category: 'Player Props',
      title: 'Drake Maye Interceptions',
      description: 'Total interceptions thrown by Drake Maye in Super Bowl LIX.',
      volume24h: '$88.7K', totalVolume: '$401K',
      options: [
        { name: 'Over 0.5', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
        { name: 'Under 0.5', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      ],
    },
    {
      badge: 'Drake Maye Pass Attempts', badgeColor: BADGE.violet, category: 'Player Props',
      title: 'Drake Maye Pass Attempts',
      description: 'Total pass attempts by Drake Maye in Super Bowl LIX.',
      volume24h: '$71.8K', totalVolume: '$322K',
      options: [
        { name: 'Over 29.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
        { name: 'Under 29.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      ],
    },
    {
      badge: 'Drake Maye Rushing Yards', badgeColor: BADGE.lime, category: 'Player Props',
      title: 'Drake Maye Rushing Yards',
      description: 'Total rushing yards by Drake Maye in Super Bowl LIX.',
      volume24h: '$64.3K', totalVolume: '$271K',
      options: [
        { name: 'Over 25.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 25.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Drake Maye Longest Pass', badgeColor: BADGE.orange, category: 'Player Props',
      title: 'Drake Maye Longest Pass',
      description: 'Longest completed pass by Drake Maye in Super Bowl LIX.',
      volume24h: '$58.3K', totalVolume: '$261K',
      options: [
        { name: 'Over 33.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 33.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Kenneth Walker III Rushing Yards', badgeColor: BADGE.teal, category: 'Player Props',
      title: 'Kenneth Walker III Rushing Yards',
      description: 'Total rushing yards by Kenneth Walker III in Super Bowl LIX.',
      volume24h: '$143.6K', totalVolume: '$671K',
      options: [
        { name: 'Over 71.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Under 71.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
    {
      badge: 'Kenneth Walker III Receiving Yards', badgeColor: BADGE.teal, category: 'Player Props',
      title: 'Kenneth Walker III Receiving Yards',
      description: 'Total receiving yards by Kenneth Walker III in Super Bowl LIX.',
      volume24h: '$119.4K', totalVolume: '$548K',
      options: [
        { name: 'Over 22.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 22.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Kenneth Walker III Total Scrimmage Yards', badgeColor: BADGE.teal, category: 'Player Props',
      title: 'Kenneth Walker III Total Scrimmage Yards',
      description: 'Total scrimmage yards by Kenneth Walker III in Super Bowl LIX.',
      volume24h: '$88.7K', totalVolume: '$401K',
      options: [
        { name: 'Over 99.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 99.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Kenneth Walker III Rushing Attempts', badgeColor: BADGE.teal, category: 'Player Props',
      title: 'Kenneth Walker III Rushing Attempts',
      description: 'Total rushing attempts by Kenneth Walker III in Super Bowl LIX.',
      volume24h: '$67.3K', totalVolume: '$289K',
      options: [
        { name: 'Over 18.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 18.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Rhamondre Stevenson Rushing Yards', badgeColor: BADGE.amber, category: 'Player Props',
      title: 'Rhamondre Stevenson Rushing Yards',
      description: 'Total rushing yards by Rhamondre Stevenson in Super Bowl LIX.',
      volume24h: '$112.4K', totalVolume: '$512K',
      options: [
        { name: 'Over 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Rhamondre Stevenson Receiving Yards', badgeColor: BADGE.amber, category: 'Player Props',
      title: 'Rhamondre Stevenson Receiving Yards',
      description: 'Total receiving yards by Rhamondre Stevenson in Super Bowl LIX.',
      volume24h: '$88.4K', totalVolume: '$342K',
      options: [
        { name: 'Over 23.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
        { name: 'Under 23.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      ],
    },
    {
      badge: 'Rhamondre Stevenson Total Scrimmage Yards', badgeColor: BADGE.amber, category: 'Player Props',
      title: 'Rhamondre Stevenson Total Scrimmage Yards',
      description: 'Total scrimmage yards by Rhamondre Stevenson in Super Bowl LIX.',
      volume24h: '$72.1K', totalVolume: '$298K',
      options: [
        { name: 'Over 40.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 40.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Jaxon Smith-Njigba Receiving Yards', badgeColor: BADGE.violet, category: 'Player Props',
      title: 'Jaxon Smith-Njigba Receiving Yards',
      description: 'Total receiving yards by Jaxon Smith-Njigba in Super Bowl LIX.',
      volume24h: '$136.9K', totalVolume: '$624K',
      options: [
        { name: 'Over 7.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Under 7.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
    {
      badge: 'Jaxon Smith-Njigba Longest Reception', badgeColor: BADGE.violet, category: 'Player Props',
      title: 'Jaxon Smith-Njigba Longest Reception',
      description: 'Longest reception by Jaxon Smith-Njigba in Super Bowl LIX.',
      volume24h: '$98.7K', totalVolume: '$441K',
      options: [
        { name: 'Over 28.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 28.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Jaxon Smith-Njigba Total Receptions', badgeColor: BADGE.violet, category: 'Player Props',
      title: 'Jaxon Smith-Njigba Total Receptions',
      description: 'Total receptions by Jaxon Smith-Njigba in Super Bowl LIX.',
      volume24h: '$81.7K', totalVolume: '$344K',
      options: [
        { name: 'Over 4.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 4.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Jaxon Smith-Njigba Anytime TD', badgeColor: BADGE.violet, category: 'Player Props',
      title: 'Jaxon Smith-Njigba Anytime TD',
      description: 'Will Jaxon Smith-Njigba score a touchdown in Super Bowl LIX?',
      volume24h: '$38.1K', totalVolume: '$162K',
      options: [
        { name: 'Yes', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
        { name: 'No', prob: '56%', price: '$0.56', payout: '$1.00', profit: '+$0.44' },
      ],
    },
    {
      badge: 'Kayshon Boutte Receiving Yards', badgeColor: BADGE.rose, category: 'Player Props',
      title: 'Kayshon Boutte Receiving Yards',
      description: 'Total receiving yards by Kayshon Boutte in Super Bowl LIX.',
      volume24h: '$64.3K', totalVolume: '$271K',
      options: [
        { name: 'Over 45.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 45.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Kayshon Boutte Total Receptions', badgeColor: BADGE.rose, category: 'Player Props',
      title: 'Kayshon Boutte Total Receptions',
      description: 'Total receptions by Kayshon Boutte in Super Bowl LIX.',
      volume24h: '$52.4K', totalVolume: '$224K',
      options: [
        { name: 'Over 4.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 4.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Rhamondre Stevenson Anytime TD', badgeColor: BADGE.amber, category: 'Player Props',
      title: 'Rhamondre Stevenson Anytime TD',
      description: 'Will Rhamondre Stevenson score a touchdown in Super Bowl LIX?',
      volume24h: '$44.2K', totalVolume: '$188K',
      options: [
        { name: 'Yes', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
        { name: 'No', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
      ],
    },
    {
      badge: 'Sam Darnold Anytime TD', badgeColor: BADGE.cyan, category: 'Player Props',
      title: 'Sam Darnold Anytime TD',
      description: 'Will Sam Darnold score a touchdown in Super Bowl LIX?',
      volume24h: '$38.1K', totalVolume: '$162K',
      options: [
        { name: 'Yes', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
        { name: 'No', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
      ],
    },
    {
      badge: 'Kenneth Walker III Anytime TD', badgeColor: BADGE.teal, category: 'Player Props',
      title: 'Kenneth Walker III Anytime TD',
      description: 'Will Kenneth Walker III score a touchdown in Super Bowl LIX?',
      volume24h: '$61.2K', totalVolume: '$278K',
      options: [
        { name: 'Yes', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
        { name: 'No', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      ],
    },
    {
      badge: 'Seahawks Total Rushing Yards', badgeColor: BADGE.cyan, category: 'Player Props',
      title: 'Seahawks Total Rushing Yards',
      description: 'Total rushing yards by Seattle in Super Bowl LIX.',
      volume24h: '$78.1K', totalVolume: '$312K',
      options: [
        { name: 'Over 115.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Under 115.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
    {
      badge: 'Patriots Total Sacks', badgeColor: BADGE.orange, category: 'Player Props',
      title: 'Patriots Total Sacks',
      description: 'Total sacks recorded by New England defense in Super Bowl LIX.',
      volume24h: '$54.8K', totalVolume: '$241K',
      options: [
        { name: 'Over 2.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
        { name: 'Under 2.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      ],
    },
    {
      badge: 'Total Turnovers in Game', badgeColor: BADGE.lime, category: 'Player Props',
      title: 'Total Turnovers in Game',
      description: 'Combined turnovers by both teams in Super Bowl LIX.',
      volume24h: '$67.3K', totalVolume: '$289K',
      options: [
        { name: 'Over 2.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 2.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Seahawks Total Rushing Yards', badgeColor: BADGE.cyan, category: 'Player Props',
      title: 'Seahawks Total Rushing Yards in Game',
      description: 'Total rushing yards by Seattle offense in Super Bowl LIX.',
      volume24h: '$58.3K', totalVolume: '$261K',
      options: [
        { name: 'Over 115.5', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
        { name: 'Under 115.5', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
      ],
    },
    {
      badge: 'Patriots Total Rushing Yards', badgeColor: BADGE.rose, category: 'Player Props',
      title: 'Patriots Total Rushing Yards in Game',
      description: 'Total rushing yards by New England offense in Super Bowl LIX.',
      volume24h: '$48.7K', totalVolume: '$214K',
      options: [
        { name: 'Over 95.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
        { name: 'Under 95.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
      ],
    },
    {
      badge: 'A.J. Barner Receiving Yards', badgeColor: BADGE.sky, category: 'Player Props',
      title: 'A.J. Barner Receiving Yards',
      description: 'Total receiving yards by A.J. Barner in Super Bowl LIX.',
      volume24h: '$42.1K', totalVolume: '$188K',
      options: [
        { name: 'Over 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Rashid Shaheed Receiving Yards', badgeColor: BADGE.gold, category: 'Player Props',
      title: 'Rashid Shaheed Receiving Yards',
      description: 'Total receiving yards by Rashid Shaheed in Super Bowl LIX.',
      volume24h: '$32.1K', totalVolume: '$142K',
      options: [
        { name: 'Over 42.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 42.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
  ],
  'Touchdown Props': [
    {
      badge: 'First Touchdown Scorer', badgeColor: BADGE.rose, category: 'Touchdown Props',
      title: 'First Touchdown Scorer',
      description: 'Which player will score the very first touchdown of Super Bowl LIX?',
      volume24h: '$176.9K', totalVolume: '$890K',
      options: [
        { name: 'Kenneth Walker III', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
        { name: 'Jaxon Smith-Njigba', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
        { name: 'Rhamondre Stevenson', prob: '11%', price: '$0.11', payout: '$1.00', profit: '+$0.89' },
        { name: 'Drake Maye', prob: '9%', price: '$0.09', payout: '$1.00', profit: '+$0.91' },
        { name: 'Kayshon Boutte', prob: '8%', price: '$0.08', payout: '$1.00', profit: '+$0.92' },
        { name: 'Other', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
      ],
    },
    {
      badge: 'Drake Maye Anytime TD', badgeColor: BADGE.amber, category: 'Touchdown Props',
      title: 'Drake Maye Anytime TD',
      description: 'Will Drake Maye score a rushing or receiving touchdown in Super Bowl LIX?',
      volume24h: '$112.4K', totalVolume: '$512K',
      options: [
        { name: 'Yes', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
        { name: 'No', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
      ],
    },
    {
      badge: 'Super Bowl MVP', badgeColor: BADGE.gold, category: 'Touchdown Props',
      title: 'Super Bowl MVP',
      description: 'Who will be named the Most Valuable Player of Super Bowl LIX?',
      volume24h: '$241.7K', totalVolume: '$1.5M',
      options: [
        { name: 'Sam Darnold (QB)', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
        { name: 'Drake Maye (QB)', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
        { name: 'Kenneth Walker III (RB)', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
        { name: 'Jaxon Smith-Njigba (WR)', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
        { name: 'Other', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      ],
    },
    {
      badge: 'Opening Coin Toss', badgeColor: BADGE.amber, category: 'Touchdown Props',
      title: 'Opening Coin Toss',
      description: 'Result of the opening coin toss of Super Bowl LIX.',
      volume24h: '$67.3K', totalVolume: '$289K',
      options: [
        { name: 'Heads', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Tails', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
  ],
  'MVP Props': [
    {
      badge: 'Super Bowl MVP', badgeColor: BADGE.gold, category: 'MVP Props',
      title: 'Super Bowl MVP',
      description: 'Who will be named the Most Valuable Player of Super Bowl LIX?',
      volume24h: '$241.7K', totalVolume: '$1.5M',
      options: [
        { name: 'Sam Darnold (QB)', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
        { name: 'Drake Maye (QB)', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
        { name: 'Kenneth Walker III (RB)', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
        { name: 'Jaxon Smith-Njigba (WR)', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
        { name: 'Rhamondre Stevenson (RB)', prob: '7%', price: '$0.07', payout: '$1.00', profit: '+$0.93' },
        { name: 'Other', prob: '5%', price: '$0.05', payout: '$1.00', profit: '+$0.95' },
      ],
    },
    {
      badge: 'Offensive MVP', badgeColor: BADGE.amber, category: 'MVP Props',
      title: 'Offensive Player of the Game',
      description: 'Which offensive player will have the biggest impact in Super Bowl LIX?',
      volume24h: '$134.8K', totalVolume: '$621K',
      options: [
        { name: 'Sam Darnold', prob: '32%', price: '$0.32', payout: '$1.00', profit: '+$0.68' },
        { name: 'Drake Maye', prob: '26%', price: '$0.26', payout: '$1.00', profit: '+$0.74' },
        { name: 'Kenneth Walker III', prob: '18%', price: '$0.18', payout: '$1.00', profit: '+$0.82' },
        { name: 'Jaxon Smith-Njigba', prob: '13%', price: '$0.13', payout: '$1.00', profit: '+$0.87' },
        { name: 'Other', prob: '11%', price: '$0.11', payout: '$1.00', profit: '+$0.89' },
      ],
    },
    {
      badge: 'Defensive MVP', badgeColor: BADGE.rose, category: 'MVP Props',
      title: 'Defensive Player of the Game',
      description: 'Which defensive player will have the biggest impact in Super Bowl LIX?',
      volume24h: '$88.3K', totalVolume: '$374K',
      options: [
        { name: 'Uchenna Nwosu (SEA)', prob: '22%', price: '$0.22', payout: '$1.00', profit: '+$0.78' },
        { name: 'Bobby Wagner (SEA)', prob: '19%', price: '$0.19', payout: '$1.00', profit: '+$0.81' },
        { name: 'Christian Barmore (NE)', prob: '17%', price: '$0.17', payout: '$1.00', profit: '+$0.83' },
        { name: 'Jabrill Peppers (NE)', prob: '15%', price: '$0.15', payout: '$1.00', profit: '+$0.85' },
        { name: 'Other', prob: '27%', price: '$0.27', payout: '$1.00', profit: '+$0.73' },
      ],
    },
  ],
  'Novelty Props': [
    {
      badge: 'National Anthem Length', badgeColor: BADGE.gold, category: 'Novelty Props',
      title: 'National Anthem Length (Charlie Charles)',
      description: 'Duration of the national anthem performance by Charlie Charles at Super Bowl LIX.',
      volume24h: '$52.4K', totalVolume: '$224K',
      options: [
        { name: 'Over 119.5 secs', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
        { name: 'Under 119.5 secs', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
      ],
    },
    {
      badge: 'Gatorade Shower Color', badgeColor: BADGE.cyan, category: 'Novelty Props',
      title: 'Gatorade Shower Color',
      description: 'Color of Gatorade dumped on winning coach at Super Bowl LIX.',
      volume24h: '$88.4K', totalVolume: '$342K',
      options: [
        { name: 'Orange', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
        { name: 'Yellow/Lime/Green', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
        { name: 'Clear/Water', prob: '22%', price: '$0.22', payout: '$1.00', profit: '+$0.78' },
        { name: 'Blue', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      ],
    },
    {
      badge: 'Players/Coach Crying', badgeColor: BADGE.rose, category: 'Novelty Props',
      title: 'Players/Coach Crying During Game',
      description: 'Will players cry during the Super Bowl LIX broadcast?',
      volume24h: '$64.3K', totalVolume: '$271K',
      options: [
        { name: 'Yes', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
        { name: 'No', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      ],
    },
    {
      badge: 'Will There Be a 2-Point Conversion?', badgeColor: BADGE.violet, category: 'Novelty Props',
      title: 'Will There Be a 2-Point Conversion?',
      description: 'Will either team attempt and convert a 2-point conversion in Super Bowl LIX?',
      volume24h: '$61.2K', totalVolume: '$278K',
      options: [
        { name: 'Yes', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
        { name: 'No', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
      ],
    },
    {
      badge: 'Total Points in Game', badgeColor: BADGE.amber, category: 'Novelty Props',
      title: 'Total Points in Game',
      description: 'Combined total points scored in Super Bowl LIX.',
      volume24h: '$78.1K', totalVolume: '$312K',
      options: [
        { name: 'Over 7.5', prob: '96%', price: '$0.96', payout: '$1.00', profit: '+$0.04' },
        { name: 'Under 7.5', prob: '4%', price: '$0.04', payout: '$1.00', profit: '+$0.96' },
      ],
    },
    {
      badge: 'Will There Be a Successful Challenge?', badgeColor: BADGE.teal, category: 'Novelty Props',
      title: 'Will There Be a Successful Challenge?',
      description: 'Will a coach\'s challenge be overturned in Super Bowl LIX?',
      volume24h: '$54.8K', totalVolume: '$241K',
      options: [
        { name: 'Yes', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
        { name: 'No', prob: '42%', price: '$0.42', payout: '$1.00', profit: '+$0.58' },
      ],
    },
    {
      badge: 'Total Penalties in Game', badgeColor: BADGE.orange, category: 'Novelty Props',
      title: 'Total Penalties in Game',
      description: 'Combined penalties called in Super Bowl LIX.',
      volume24h: '$42.6K', totalVolume: '$188K',
      options: [
        { name: 'Over 9.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 9.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Longest Touchdown', badgeColor: BADGE.lime, category: 'Novelty Props',
      title: 'Longest Touchdown',
      description: 'Length of the longest touchdown scored in Super Bowl LIX.',
      volume24h: '$38.1K', totalVolume: '$162K',
      options: [
        { name: 'Over 42.5 yards', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
        { name: 'Under 42.5 yards', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      ],
    },
    {
      badge: 'Kenneth Walker III 2+ Rushing TDs', badgeColor: BADGE.teal, category: 'Novelty Props',
      title: 'Kenneth Walker III 2+ Rushing TDs',
      description: 'Will Kenneth Walker III score 2 or more rushing touchdowns in Super Bowl LIX?',
      volume24h: '$44.2K', totalVolume: '$188K',
      options: [
        { name: 'Yes', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
        { name: 'No', prob: '72%', price: '$0.72', payout: '$1.00', profit: '+$0.28' },
      ],
    },
    {
      badge: 'A.J. Barner Receiving Yards', badgeColor: BADGE.sky, category: 'Novelty Props',
      title: 'A.J. Barner Receiving Yards',
      description: 'Total receiving yards by A.J. Barner in Super Bowl LIX.',
      volume24h: '$36.4K', totalVolume: '$158K',
      options: [
        { name: 'Over 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Rashid Shaheed Receiving Yards', badgeColor: BADGE.gold, category: 'Novelty Props',
      title: 'Rashid Shaheed Receiving Yards',
      description: 'Total receiving yards by Rashid Shaheed in Super Bowl LIX.',
      volume24h: '$32.1K', totalVolume: '$142K',
      options: [
        { name: 'Over 42.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 42.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Seahawks Total Passing Yards', badgeColor: BADGE.cyan, category: 'Novelty Props',
      title: 'Seahawks Total Passing Yards',
      description: 'Total passing yards by Seattle offense in Super Bowl LIX.',
      volume24h: '$58.3K', totalVolume: '$261K',
      options: [
        { name: 'Over 240.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 240.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Patriots Total Passing Yards', badgeColor: BADGE.rose, category: 'Novelty Props',
      title: 'Patriots Total Passing Yards',
      description: 'Total passing yards by New England offense in Super Bowl LIX.',
      volume24h: '$48.7K', totalVolume: '$214K',
      options: [
        { name: 'Over 225.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 225.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
  ],
  'Team Props': [
    {
      badge: 'Team to Score First', badgeColor: BADGE.teal, category: 'Team Props',
      title: 'Team to Score First',
      description: 'Which team will put the first points on the board in Super Bowl LIX?',
      volume24h: '$88.9K', totalVolume: '$398K',
      options: [
        { name: 'Seahawks', prob: '55%', price: '$0.55', payout: '$1.00', profit: '+$0.45' },
        { name: 'Patriots', prob: '45%', price: '$0.45', payout: '$1.00', profit: '+$0.55' },
      ],
    },
    {
      badge: 'Team to Score Last', badgeColor: BADGE.amber, category: 'Team Props',
      title: 'Team to Score Last',
      description: 'Which team will score the final points of Super Bowl LIX?',
      volume24h: '$76.4K', totalVolume: '$341K',
      options: [
        { name: 'Seahawks', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
        { name: 'Patriots', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
      ],
    },
    {
      badge: 'Longest Field Goal', badgeColor: BADGE.violet, category: 'Team Props',
      title: 'Longest Field Goal',
      description: 'Length of the longest field goal made in Super Bowl LIX.',
      volume24h: '$58.3K', totalVolume: '$261K',
      options: [
        { name: 'Over 47.5 yards', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 47.5 yards', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Will There Be a Missed FG?', badgeColor: BADGE.rose, category: 'Team Props',
      title: 'Will There Be a Missed Field Goal?',
      description: 'Will either kicker miss a field goal attempt in Super Bowl LIX?',
      volume24h: '$44.2K', totalVolume: '$188K',
      options: [
        { name: 'Yes', prob: '42%', price: '$0.42', payout: '$1.00', profit: '+$0.58' },
        { name: 'No', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
      ],
    },
  ],
  'Quarter Props': [
    {
      badge: '1st Quarter Total Points', badgeColor: BADGE.sky, category: 'Quarter Props',
      title: '1st Quarter Total Points',
      description: 'Combined points scored in the 1st quarter of Super Bowl LIX.',
      volume24h: '$88.9K', totalVolume: '$398K',
      options: [
        { name: 'Over 9.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 9.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: '2nd Quarter Total Points', badgeColor: BADGE.teal, category: 'Quarter Props',
      title: '2nd Quarter Total Points',
      description: 'Combined points scored in the 2nd quarter of Super Bowl LIX.',
      volume24h: '$76.4K', totalVolume: '$341K',
      options: [
        { name: 'Over 10.5', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Under 10.5', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
    {
      badge: '3rd Quarter Total Points', badgeColor: BADGE.amber, category: 'Quarter Props',
      title: '3rd Quarter Total Points',
      description: 'Combined points scored in the 3rd quarter of Super Bowl LIX.',
      volume24h: '$64.3K', totalVolume: '$271K',
      options: [
        { name: 'Over 10.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 10.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: '4th Quarter Total Points', badgeColor: BADGE.violet, category: 'Quarter Props',
      title: '4th Quarter Total Points',
      description: 'Combined points scored in the 4th quarter of Super Bowl LIX.',
      volume24h: '$58.3K', totalVolume: '$261K',
      options: [
        { name: 'Over 11.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 11.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Team to Score First in 2nd Half', badgeColor: BADGE.rose, category: 'Quarter Props',
      title: 'Team to Score First in 2nd Half',
      description: 'Which team will score first after halftime in Super Bowl LIX?',
      volume24h: '$48.7K', totalVolume: '$214K',
      options: [
        { name: 'Seahawks', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
        { name: 'Patriots', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
      ],
    },
    {
      badge: 'Team to Score Last in 1st Half', badgeColor: BADGE.lime, category: 'Quarter Props',
      title: 'Team to Score Last in 1st Half',
      description: 'Which team will score the final points before halftime in Super Bowl LIX?',
      volume24h: '$42.1K', totalVolume: '$188K',
      options: [
        { name: 'Seahawks', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
        { name: 'Patriots', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      ],
    },
  ],
  'Special Props': [
    {
      badge: 'Will There Be a 2-Point Conversion?', badgeColor: BADGE.violet, category: 'Special Props',
      title: 'Will There Be a 2-Point Conversion?',
      description: 'Will either team attempt and convert a 2-point conversion in Super Bowl LIX?',
      volume24h: '$61.2K', totalVolume: '$278K',
      options: [
        { name: 'Yes', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
        { name: 'No', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
      ],
    },
    {
      badge: 'Total Points in Game', badgeColor: BADGE.amber, category: 'Special Props',
      title: 'Total Points in Game',
      description: 'Combined total points scored in Super Bowl LIX.',
      volume24h: '$78.1K', totalVolume: '$312K',
      options: [
        { name: 'Over 7.5', prob: '96%', price: '$0.96', payout: '$1.00', profit: '+$0.04' },
        { name: 'Under 7.5', prob: '4%', price: '$0.04', payout: '$1.00', profit: '+$0.96' },
      ],
    },
    {
      badge: 'Will There Be a Successful Challenge?', badgeColor: BADGE.teal, category: 'Special Props',
      title: 'Will There Be a Successful Challenge?',
      description: 'Will a coach\'s challenge be overturned in Super Bowl LIX?',
      volume24h: '$54.8K', totalVolume: '$241K',
      options: [
        { name: 'Yes', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
        { name: 'No', prob: '42%', price: '$0.42', payout: '$1.00', profit: '+$0.58' },
      ],
    },
    {
      badge: 'Total Penalties in Game', badgeColor: BADGE.orange, category: 'Special Props',
      title: 'Total Penalties in Game',
      description: 'Combined penalties called in Super Bowl LIX.',
      volume24h: '$42.6K', totalVolume: '$188K',
      options: [
        { name: 'Over 9.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 9.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Longest Touchdown', badgeColor: BADGE.lime, category: 'Special Props',
      title: 'Longest Touchdown',
      description: 'Length of the longest touchdown scored in Super Bowl LIX.',
      volume24h: '$38.1K', totalVolume: '$162K',
      options: [
        { name: 'Over 42.5 yards', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
        { name: 'Under 42.5 yards', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      ],
    },
    {
      badge: 'Kenneth Walker III 2+ Rushing TDs', badgeColor: BADGE.teal, category: 'Special Props',
      title: 'Kenneth Walker III 2+ Rushing TDs',
      description: 'Will Kenneth Walker III score 2 or more rushing touchdowns in Super Bowl LIX?',
      volume24h: '$44.2K', totalVolume: '$188K',
      options: [
        { name: 'Yes', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
        { name: 'No', prob: '72%', price: '$0.72', payout: '$1.00', profit: '+$0.28' },
      ],
    },
    {
      badge: 'A.J. Barner Receiving Yards', badgeColor: BADGE.sky, category: 'Special Props',
      title: 'A.J. Barner Receiving Yards',
      description: 'Total receiving yards by A.J. Barner in Super Bowl LIX.',
      volume24h: '$36.4K', totalVolume: '$158K',
      options: [
        { name: 'Over 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 30.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
    {
      badge: 'Rashid Shaheed Receiving Yards', badgeColor: BADGE.gold, category: 'Special Props',
      title: 'Rashid Shaheed Receiving Yards',
      description: 'Total receiving yards by Rashid Shaheed in Super Bowl LIX.',
      volume24h: '$32.1K', totalVolume: '$142K',
      options: [
        { name: 'Over 42.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
        { name: 'Under 42.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      ],
    },
    {
      badge: 'Seahawks Total Passing Yards', badgeColor: BADGE.cyan, category: 'Special Props',
      title: 'Seahawks Total Passing Yards in Game',
      description: 'Total passing yards by Seattle offense in Super Bowl LIX.',
      volume24h: '$58.3K', totalVolume: '$261K',
      options: [
        { name: 'Over 240.5', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
        { name: 'Under 240.5', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      ],
    },
    {
      badge: 'Patriots Total Passing Yards', badgeColor: BADGE.rose, category: 'Special Props',
      title: 'Patriots Total Passing Yards in Game',
      description: 'Total passing yards by New England offense in Super Bowl LIX.',
      volume24h: '$48.7K', totalVolume: '$214K',
      options: [
        { name: 'Over 225.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
        { name: 'Under 225.5', prob: '50%', price: '$0.50', payout: '$1.00', profit: '+$0.50' },
      ],
    },
  ],
};

const TABS = [
  'All Props', 'Game Props', 'Player Props', 'Touchdown Props',
  'MVP Props', 'Novelty Props', 'Team Props', 'Quarter Props', 'Special Props',
];

const SUBNAV = [
  { key: 'all', label: 'All', icon: 'ri-apps-2-line', path: '/super-bowl-props' },
  { key: 'spreads', label: 'Spreads', icon: 'ri-bar-chart-grouped-line', path: '/markets/spreads' },
  { key: 'money-lines', label: 'Money Lines', icon: 'ri-copper-coin-line', path: '/markets/money-lines' },
  { key: 'futures', label: 'Futures', icon: 'ri-calendar-event-line', path: '/markets/futures' },
  { key: 'parlays', label: 'Parlays', icon: 'ri-stack-line', path: '/markets/parlays' },
  { key: 'player-props', label: 'Player Props', icon: 'ri-user-star-line', path: '/markets/player-props' },
];

// Map each market title/badge to a sub-nav key
const SUBNAV_MAP: Record<string, string> = {
  'Game Spread': 'spreads',
  'Game Total Points': 'spreads',
  'Seahawks Team Total': 'spreads',
  'Patriots Team Total': 'spreads',
  'First Half Total': 'spreads',
  'Second Half Total': 'spreads',
  'Highest Scoring Quarter': 'spreads',
  '1st Quarter Total Points': 'spreads',
  '2nd Quarter Total Points': 'spreads',
  '3rd Quarter Total Points': 'spreads',
  '4th Quarter Total Points': 'spreads',
  'Super Bowl MVP': 'futures',
  'Offensive MVP': 'futures',
  'Defensive MVP': 'futures',
  'Sam Darnold Passing Yards': 'player-props',
  'Sam Darnold Passing TDs': 'player-props',
  'Sam Darnold Interceptions': 'player-props',
  'Sam Darnold Pass Attempts': 'player-props',
  'Sam Darnold Completions': 'player-props',
  'Drake Maye Passing Yards': 'player-props',
  'Drake Maye Passing TDs': 'player-props',
  'Drake Maye Interceptions': 'player-props',
  'Drake Maye Pass Attempts': 'player-props',
  'Drake Maye Rushing Yards': 'player-props',
  'Drake Maye Longest Pass': 'player-props',
  'Kenneth Walker III Rushing Yards': 'player-props',
  'Kenneth Walker III Receiving Yards': 'player-props',
  'Kenneth Walker III Total Scrimmage Yards': 'player-props',
  'Kenneth Walker III Rushing Attempts': 'player-props',
  'Kenneth Walker III Anytime TD': 'player-props',
  'Rhamondre Stevenson Rushing Yards': 'player-props',
  'Rhamondre Stevenson Receiving Yards': 'player-props',
  'Rhamondre Stevenson Total Scrimmage Yards': 'player-props',
  'Rhamondre Stevenson Anytime TD': 'player-props',
  'Jaxon Smith-Njigba Receiving Yards': 'player-props',
  'Jaxon Smith-Njigba Longest Reception': 'player-props',
  'Jaxon Smith-Njigba Total Receptions': 'player-props',
  'Jaxon Smith-Njigba Anytime TD': 'player-props',
  'Kayshon Boutte Receiving Yards': 'player-props',
  'Kayshon Boutte Total Receptions': 'player-props',
  'Kayshon Boutte Anytime TD': 'player-props',
  'Seahawks Total Rushing Yards': 'player-props',
  'Patriots Total Sacks': 'player-props',
  'Total Turnovers in Game': 'player-props',
  'Seahawks Total Rushing Yards in Game': 'player-props',
  'Patriots Total Rushing Yards in Game': 'player-props',
  'A.J. Barner Receiving Yards': 'player-props',
  'Rashid Shaheed Receiving Yards': 'player-props',
  'First Touchdown Scorer': 'money-lines',
  'Drake Maye Anytime TD': 'money-lines',
  'Team to Score First': 'money-lines',
  'Team to Score Last': 'money-lines',
  'Team to Score First in 2nd Half': 'money-lines',
  'Team to Score Last in 1st Half': 'money-lines',
};

export default function SuperBowlPropsPage() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('All Props');
  const [activeSubNav, setActiveSubNav] = useState('all');
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const marketsDropdownRef = useRef<HTMLDivElement>(null);
  const { items: slipItems, toggleSlip } = useBetSlip();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine active sub-nav based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/super-bowl-props') {
      setActiveSubNav('all');
    } else if (path === '/markets/spreads') {
      setActiveSubNav('spreads');
    } else if (path === '/markets/money-lines') {
      setActiveSubNav('money-lines');
    } else if (path === '/markets/futures') {
      setActiveSubNav('futures');
    } else if (path === '/markets/parlays') {
      setActiveSubNav('parlays');
    } else if (path === '/markets/player-props') {
      setActiveSubNav('player-props');
    }
  }, [location.pathname]);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-super-bowl-props';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Super Bowl LIX Props — L2L Sports Prediction Market',
        description: 'Trade Super Bowl LIX prop bets. Seattle Seahawks vs New England Patriots. Game props, player props, touchdown props and more.',
        url: `${siteUrl}/super-bowl-props`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: 'Super Bowl LIX',
        sport: 'American Football',
        competitor: [
          { '@type': 'SportsTeam', name: 'Seattle Seahawks' },
          { '@type': 'SportsTeam', name: 'New England Patriots' },
        ],
        startDate: '2025-02-09',
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const allFlat = Object.values(allMarkets).flat();
  const byTab = activeTab === 'All Props' ? allFlat : (allMarkets[activeTab] || []);

  const bySubNav = activeSubNav === 'all'
    ? byTab
    : activeSubNav === 'parlays'
      ? []
      : byTab.filter(m => SUBNAV_MAP[m.title] === activeSubNav || SUBNAV_MAP[m.badge] === activeSubNav);

  const displayed = searchQuery.trim()
    ? bySubNav.filter(m => {
        const q = searchQuery.toLowerCase();
        return (
          m.title.toLowerCase().includes(q) ||
          m.badge.toLowerCase().includes(q) ||
          m.category.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.options.some(o => o.name.toLowerCase().includes(q))
        );
      })
    : bySubNav;

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50 transition-colors duration-300" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>

              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen(o => !o)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl shadow-black/40 overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    <div className="p-1.5 space-y-0.5">
                      {[
                        { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
                        { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
                        { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
                        { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
                        { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
                      ].map(item => (
                        <a key={item.href} href={item.href} onClick={() => setMarketsDropdownOpen(false)}>
                          <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-700/60 hover:text-white transition-colors cursor-pointer">
                            <i className={`${item.icon} text-teal-400 w-4 h-4 flex items-center justify-center`}></i>
                            {item.label}
                          </div>
                        </a>
                      ))}
                    </div>
                    <div className="border-t border-slate-700/50 p-1.5">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-teal-400 hover:bg-teal-500/10 transition-colors cursor-pointer font-medium">
                          <i className="ri-apps-2-line w-4 h-4 flex items-center justify-center"></i>
                          All Markets
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <a href="/why-rtj"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>Why L2L</div></a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button
                onClick={() => toggleSlip()}
                className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer"
                title="Bet Slip"
              >
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                {slipItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {slipItems.length}
                  </span>
                )}
              </button>
              <a href="/my-account">
                <div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
                { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: '↳ Spreads' },
                { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: '↳ Money Lines' },
                { href: '/markets/futures', icon: 'ri-calendar-event-line', label: '↳ Futures' },
                { href: '/markets/parlays', icon: 'ri-stack-line', label: '↳ Parlays' },
                { href: '/markets/player-props', icon: 'ri-user-star-line', label: '↳ Player Props' },
                { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        badgeLabel="Live Super Bowl Markets"
        badgeColor="amber"
        title="Super Bowl LIX Props"
        subtitle="Seattle Seahawks vs. New England Patriots — We're so tired of paying the juice. Join us in the prediction market revolution."
        backgroundImage="https://public.readdy.ai/ai/img_res/800809247f7b425c1408647df3d2c7f7.jpg"
      />

      {/* Stats Bar */}
      <div className="border-y border-slate-700/50" style={{ background: 'rgba(30,30,30,0.8)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { label: 'Active Markets', value: String(allFlat.length), icon: 'ri-bar-chart-2-line', color: 'text-teal-400' },
              { label: '24h Volume', value: '$3.4M', icon: 'ri-line-chart-line', color: 'text-emerald-400' },
              { label: 'Total Volume', value: '$18.2M', icon: 'ri-water-flash-line', color: 'text-amber-400' },
              { label: 'Prop Categories', value: String(TABS.length - 1), icon: 'ri-football-line', color: 'text-rose-400' },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <i className={`${stat.icon} text-xl ${stat.color}`}></i>
                <div className={`text-xl font-extrabold ${stat.color}`}>{stat.value}</div>
                <div className="text-xs text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky Tab Bar */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50 overflow-x-auto" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 py-2 min-w-max">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setActiveSubNav('all'); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-teal-500/20 border-teal-500/50 text-teal-400'
                    : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="sticky top-[105px] z-[39] border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 py-2">
            {/* Shortcut tabs — scrollable */}
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-none flex-1 min-w-0">
              {SUBNAV.map(sub => (
                <a key={sub.key} href={sub.path}>
                  <button
                    className={`flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                      activeSubNav === sub.key
                        ? 'bg-slate-700 text-white border border-slate-500/60'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                    }`}
                  >
                    <i className={`${sub.icon} text-sm ${activeSubNav === sub.key ? 'text-teal-400' : 'text-slate-500'}`}></i>
                    {sub.label}
                  </button>
                </a>
              ))}
            </div>

            {/* Search box — inline on sm+ screens only */}
            <div className="hidden sm:flex flex-shrink-0 ml-3">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search markets…"
                  className="w-44 lg:w-52 bg-slate-800/80 border border-slate-700/60 rounded-lg pl-8 pr-8 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/60 focus:ring-1 focus:ring-teal-500/30 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mobile search — full width row below tabs */}
          <div className="sm:hidden pb-2">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search markets…"
                className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg pl-8 pr-8 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/60 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          </div>

          {searchQuery.trim() && (
            <p className="text-xs text-slate-500 pb-1.5 ml-1">
              {displayed.length} result{displayed.length !== 1 ? 's' : ''} for &ldquo;<span className="text-teal-400">{searchQuery}</span>&rdquo;
            </p>
          )}
        </div>
      </div>

      {/* Markets Grid */}
      <section className="py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">{activeTab === 'All Props' ? 'All Super Bowl Markets' : `${activeTab} Markets`}</h2>
              <p className="text-slate-400 text-sm mt-1">{displayed.length} market{displayed.length !== 1 ? 's' : ''} available</p>
            </div>
          </div>

          {activeSubNav === 'parlays' ? (
            <div className="text-center py-24 text-slate-500">
              <i className="ri-stack-line text-5xl mb-4 block text-slate-600"></i>
              <p className="text-slate-400 font-medium mb-1">
                Parlays Coming Soon
              </p>
              <p className="text-sm text-slate-600 max-w-sm mx-auto">Multi-leg parlay markets are on the way. Check back soon for combined outcome trading.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {displayed.map((market, idx) => (
                  <SuperBowlBetCard key={`${activeTab}-${activeSubNav}-${idx}`} {...market} />
                ))}
              </div>
              {displayed.length === 0 && (
                <div className="text-center py-20 text-slate-500">
                  <i className="ri-search-line text-4xl mb-3 block"></i>
                  <p className="text-slate-400 font-medium mb-1">
                    {searchQuery.trim() ? `No markets found for "${searchQuery}"` : 'No markets found for this filter.'}
                  </p>
                  {searchQuery.trim() && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="mt-3 text-sm text-teal-400 hover:text-teal-300 underline cursor-pointer transition-colors"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl border border-amber-500/20 p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(43,43,43,0.9) 50%, rgba(20,184,166,0.05) 100%)' }}>
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-teal-500/5"></div>
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white">Ready to Trade the Big Game?</h3>
              <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                No house edge. No bet limits. Just pure market-driven pricing on every Super Bowl outcome.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/">
                  <button className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-gradient-to-r from-amber-400 to-teal-400 hover:from-amber-500 hover:to-teal-500 text-slate-900 font-bold rounded-lg shadow-lg shadow-amber-500/20 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-home-line"></i>
                    Back to Home
                  </button>
                </a>
                <a href="/march-madness">
                  <button className="inline-flex items-center justify-center gap-2 px-7 py-3 border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-basketball-line"></i>
                    March Madness
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
